import React, { useState, useEffect, useMemo } from 'react';
import { FiEdit2, FiX, FiFileText, FiPlus } from 'react-icons/fi';
import { getProviders, createProvider, updateProvider, deleteProvider } from '../../../services/api';
import '../../../styles/ModuleTable.css';

const BLANK = {
  ruc: '',
  name: '',
  contact_person: '',
  account_number: '',
  address: '',
  phone: '',
  celular: '',
  web: '',
  email: '',
};

const ITEMS_OPTIONS = [10, 20, 50, 100];

function exportToExcel(headers, rows, filename) {
  let html = `<table><thead><tr>${headers.map(h => `<th>${h}</th>`).join('')}</tr></thead><tbody>`;
  rows.forEach(r => { html += `<tr>${r.map(c => `<td>${c ?? ''}</td>`).join('')}</tr>`; });
  html += '</tbody></table>';
  const blob = new Blob(['﻿' + html], { type: 'application/vnd.ms-excel' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `${filename}.xls`;
  a.click();
  URL.revokeObjectURL(a.href);
}

function exportToPDF(title, headers, rows) {
  const win = window.open('', '_blank');
  const trs = rows.map((r, i) =>
    `<tr style="background:${i % 2 === 0 ? '#fff' : '#f0f7ff'}">${r.map(c => `<td>${c ?? ''}</td>`).join('')}</tr>`
  ).join('');
  win.document.write(`
    <html><head><title>${title}</title>
    <style>
      body{font-family:Arial,sans-serif;font-size:11px;padding:16px;color:#222}
      h2{color:#17375e;font-size:13px;margin-bottom:8px;text-transform:uppercase}
      table{border-collapse:collapse;width:100%}
      th{background:#dce6f1;color:#17375e;padding:6px 8px;text-align:left;font-size:11px;border:1px solid #9ebcdf}
      td{padding:5px 8px;border:1px solid #dde8f2;font-size:11px}
      .footer{margin-top:10px;font-size:10px;color:#888;text-align:right}
    </style></head>
    <body><h2>${title}</h2>
    <table><thead><tr>${headers.map(h => `<th>${h}</th>`).join('')}</tr></thead>
    <tbody>${trs}</tbody></table>
    <div class="footer">PIVASAPOLLOS — Generado ${new Date().toLocaleString('es-PE', { timeZone: 'America/Lima' })}</div>
    <script>window.onload=()=>{window.print();}</script>
    </body></html>
  `);
  win.document.close();
}

function Paginacion({ page, totalPages, perPage, setPage, setPerPage }) {
  return (
    <div className="mt-pagination">
      <span className="pg-info">({page} of {totalPages})</span>
      <button onClick={() => setPage(1)} disabled={page === 1}>|&lt;</button>
      <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>&lt;&lt;</button>
      <span className="pg-current">{page}</span>
      <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>&gt;&gt;</button>
      <button onClick={() => setPage(totalPages)} disabled={page === totalPages}>&gt;|</button>
      <select value={perPage} onChange={e => { setPerPage(+e.target.value); setPage(1); }}>
        {ITEMS_OPTIONS.map(n => <option key={n} value={n}>{n}</option>)}
      </select>
    </div>
  );
}

function ProviderMaintenance({ businessId }) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const [filterRuc, setFilterRuc] = useState('');
  const [filterName, setFilterName] = useState('');
  const [filterRep, setFilterRep] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(50);

  useEffect(() => { load(); }, [businessId]); // eslint-disable-line

  const load = async () => {
    setLoading(true);
    try {
      const res = await getProviders(businessId);
      setData(res.data.data || []);
    } catch (e) {
      alert('Error al cargar proveedores: ' + (e.response?.data?.error || e.message));
    } finally { setLoading(false); }
  };

  const filtered = useMemo(() => {
    return data.filter(r => {
      if (filterRuc && !(r.ruc || '').includes(filterRuc)) return false;
      if (filterName && !(r.name || '').toUpperCase().includes(filterName.toUpperCase())) return false;
      if (filterRep && !(r.contact_person || '').toUpperCase().includes(filterRep.toUpperCase())) return false;
      if (search) {
        const s = search.toUpperCase();
        return (r.name || '').toUpperCase().includes(s)
          || (r.ruc || '').includes(s)
          || (r.contact_person || '').toUpperCase().includes(s)
          || (r.address || '').toUpperCase().includes(s);
      }
      return true;
    });
  }, [data, filterRuc, filterName, filterRep, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
  const paged = filtered.slice((page - 1) * perPage, page * perPage);

  const openNew = () => { setForm(BLANK); setErrors({}); setEditId(null); setModal(true); };

  // Handlers con filtro numérico
  const handleRuc = (e) => {
    const val = e.target.value.replace(/[^0-9]/g, '').slice(0, 11);
    setForm(p => ({ ...p, ruc: val }));
    setErrors(p => ({ ...p, ruc: '' }));
  };
  const handlePhone = (e) => {
    const val = e.target.value.replace(/[^0-9]/g, '');
    setForm(p => ({ ...p, phone: val }));
    setErrors(p => ({ ...p, phone: '' }));
  };
  const handleCelular = (e) => {
    const val = e.target.value.replace(/[^0-9]/g, '');
    setForm(p => ({ ...p, celular: val }));
    setErrors(p => ({ ...p, celular: '' }));
  };

  const validate = () => {
    const errs = {};
    if (form.ruc && form.ruc.length !== 11)
      errs.ruc = 'El RUC debe tener exactamente 11 dígitos';
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      errs.email = 'Correo electrónico inválido';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const openEdit = (row) => {
    setErrors({});
    setForm({
      ruc: row.ruc || '',
      name: row.name || '',
      contact_person: row.contact_person || '',
      account_number: row.account_number || '',
      address: row.address || '',
      phone: row.phone || '',
      celular: row.celular || '',
      web: row.web || '',
      email: row.email || '',
    });
    setEditId(row.id);
    setModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Desea eliminar este proveedor?')) return;
    try {
      await deleteProvider(id, businessId);
      load();
    } catch (e) { alert('Error: ' + (e.response?.data?.error || e.message)); }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSaving(true);
    try {
      if (editId) {
        await updateProvider(editId, businessId, form);
      } else {
        await createProvider(businessId, form);
      }
      setModal(false);
      load();
    } catch (e) {
      alert('Error: ' + (e.response?.data?.error || e.message));
    } finally { setSaving(false); }
  };

  const fld = (k) => (v) => setForm(prev => ({ ...prev, [k]: v.target.value }));

  const excelData = () => {
    const headers = ['RUC', 'PROVEEDOR', 'REPRESENTANTE', 'DIRECCIÓN'];
    const rows = filtered.map(r => [r.ruc, r.name, r.contact_person, r.address]);
    exportToExcel(headers, rows, 'Proveedores');
  };
  const pdfData = () => {
    const headers = ['RUC', 'PROVEEDOR', 'REPRESENTANTE', 'DIRECCIÓN'];
    const rows = filtered.map(r => [r.ruc, r.name, r.contact_person, r.address]);
    exportToPDF('PROVEEDORES', headers, rows);
  };

  const ExportBar = () => (
    <div className="mt-export-bar">
      <button className="btn-excel" onClick={excelData} title="Exportar Excel">
        <span style={{ fontSize: 14 }}>🐦</span> Excel
      </button>
      <button className="btn-pdf" onClick={pdfData} title="Exportar PDF">
        <FiFileText size={12} /> PDF
      </button>
      <Paginacion page={page} totalPages={totalPages} perPage={perPage} setPage={setPage} setPerPage={setPerPage} />
    </div>
  );

  return (
    <div className="mt-container">
      {/* Toolbar */}
      <div className="mt-toolbar">
        <div className="mt-toolbar-left">
          <button className="btn-nuevo" onClick={openNew}>
            <FiPlus size={12} /> Nuevo
          </button>
        </div>
        <div className="mt-search-global">
          <span>Busqueda Global</span>
          <input
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
          />
        </div>
      </div>

      <ExportBar />

      {/* Tabla */}
      <div className="mt-table-wrap">
        {loading ? (
          <div className="mt-loading">Cargando...</div>
        ) : (
          <table className="mt-table">
            <thead>
              <tr className="mt-head-row">
                <th>RUC</th>
                <th>Proveedor</th>
                <th>Representante</th>
                <th>Direccion</th>
                <th style={{ width: 30 }}></th>
                <th style={{ width: 30 }}></th>
              </tr>
              <tr className="mt-filter-row">
                <th>
                  <input value={filterRuc} onChange={e => { setFilterRuc(e.target.value); setPage(1); }} />
                </th>
                <th>
                  <input value={filterName} onChange={e => { setFilterName(e.target.value); setPage(1); }} />
                </th>
                <th>
                  <input value={filterRep} onChange={e => { setFilterRep(e.target.value); setPage(1); }} />
                </th>
                <th></th>
                <th></th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {paged.length === 0 ? (
                <tr className="mt-empty"><td colSpan={6}>Sin registros</td></tr>
              ) : paged.map((row, i) => (
                <tr key={row.id} className={i % 2 === 0 ? 'mt-row-even' : 'mt-row-odd'}>
                  <td>{row.ruc}</td>
                  <td>{row.name}</td>
                  <td>{row.contact_person}</td>
                  <td>{row.address}</td>
                  <td className="td-actions">
                    <button className="btn-row-edit" onClick={() => openEdit(row)} title="Editar">
                      <FiEdit2 size={13} />
                    </button>
                  </td>
                  <td className="td-actions">
                    <button className="btn-row-del" onClick={() => handleDelete(row.id)} title="Eliminar">
                      <FiX size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <ExportBar />

      {/* Modal */}
      {modal && (
        <div className="mt-modal-overlay">
          <div className="mt-modal">
            <div className="mt-modal-header">
              <span>Mantenimiento de Proveedores</span>
              <button className="btn-modal-close" onClick={() => setModal(false)}>✕</button>
            </div>
            <form onSubmit={handleSave}>
              <div className="mt-modal-body">
                <div className="mt-form-grid">
                  <div className="mt-field mt-full">
                    <label>R.U.C <span className="field-hint">(11 dígitos)</span></label>
                    <input
                      value={form.ruc}
                      onChange={handleRuc}
                      inputMode="numeric"
                      maxLength={11}
                      placeholder="Ej: 20100000001"
                    />
                    {errors.ruc && <span className="field-error">{errors.ruc}</span>}
                  </div>

                  <div className="mt-field mt-full">
                    <label>Nombre Proveedor <span className="req">*</span></label>
                    <input value={form.name} onChange={fld('name')} required />
                  </div>

                  <div className="mt-field mt-full">
                    <label>Nombre Representante</label>
                    <input value={form.contact_person} onChange={fld('contact_person')} />
                  </div>

                  <div className="mt-field mt-full">
                    <label>Numero de Cuenta</label>
                    <input value={form.account_number} onChange={fld('account_number')} />
                  </div>

                  <div className="mt-field mt-full">
                    <label>Dirección</label>
                    <textarea value={form.address} onChange={fld('address')} />
                  </div>

                  <div className="mt-field">
                    <label>Teléfono <span className="field-hint">(solo números)</span></label>
                    <input
                      value={form.phone}
                      onChange={handlePhone}
                      inputMode="numeric"
                      placeholder="Ej: 014441234"
                    />
                    {errors.phone && <span className="field-error">{errors.phone}</span>}
                  </div>

                  <div className="mt-field">
                    <label>Celular <span className="field-hint">(solo números)</span></label>
                    <input
                      value={form.celular}
                      onChange={handleCelular}
                      inputMode="numeric"
                      placeholder="Ej: 987654321"
                    />
                    {errors.celular && <span className="field-error">{errors.celular}</span>}
                  </div>

                  <div className="mt-field">
                    <label>Web</label>
                    <input value={form.web} onChange={fld('web')} placeholder="www.ejemplo.com" />
                  </div>

                  <div className="mt-field">
                    <label>Email</label>
                    <input
                      value={form.email}
                      onChange={e => { fld('email')(e); setErrors(p => ({ ...p, email: '' })); }}
                      placeholder="correo@empresa.com"
                    />
                    {errors.email && <span className="field-error">{errors.email}</span>}
                  </div>
                </div>
              </div>
              <div className="mt-modal-footer">
                <button type="submit" className="btn-modificar" disabled={saving}>
                  {saving ? 'Guardando...' : 'Modificar'}
                </button>
                <button type="button" className="btn-salir" onClick={() => setModal(false)}>
                  ✕ Salir
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default ProviderMaintenance;
